import config from "../../../config";

// Add time chunking utility
const createTimeChunks = (startDate, endDate) => {
  const chunks = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Calculate total hours in the date range
  const totalHours = (end - start) / (1000 * 60 * 60);
  
  // Determine chunk size based on total duration
  let chunkHours;
  if (totalHours <= 24) {
    chunkHours = 2; // 2-hour chunks for 1 day
  } else if (totalHours <= 168) {
    chunkHours = 6; // 6-hour chunks for up to 1 week
  } else {
    chunkHours = 12; // 12-hour chunks for longer periods
  }
  
  let currentStart = new Date(start);
  
  while (currentStart < end) {
    const currentEnd = new Date(currentStart);
    currentEnd.setHours(currentEnd.getHours() + chunkHours);
    
    // Don't exceed the original end date
    if (currentEnd > end) {
      currentEnd.setTime(end.getTime());
    }
    
    chunks.push({
      startDate: currentStart.toISOString(),
      endDate: currentEnd.toISOString()
    });
    
    currentStart = new Date(currentEnd);
  }
  
  return chunks;
};

const fetchPaginatedData = async (url, baseParams, page = 1) => {
  try {
    const paginatedUrl = new URL(url);
    const params = { ...baseParams, page, pageSize: 100 };
    
    Object.entries(params).forEach(([param, value]) => {
      if (value !== undefined) {
        paginatedUrl.searchParams.append(param, value);
      }
    });

    const response = await fetch(paginatedUrl);
    const data = await response.json();
    
    return {
      data: data.data || [],
      totalPages: data.totalPages || 1,
      currentPage: data.currentPage || 1,
      hasMore: data.hasNextPage || false,
      pagination: data.pagination,
      maxSpeed: data.maxSpeed // Pass through maxSpeed for overspeed reports
    };
  } catch (error) {
    console.error("Error fetching paginated data:", error);
    throw error;
  }
};

export const fetchOverviewData = async (selectedDateRange, customDateRange) => {
  const localDataString = localStorage.getItem("userState");
  const localData = localDataString ? JSON.parse(localDataString) : null;
  const orgId = localData?.user?.organizationId;

  // Calculate date range
  let startDate, endDate;
  if (selectedDateRange === "custom" && customDateRange) {
    startDate = customDateRange.startDate;
    endDate = customDateRange.endDate;
  } else {
    // Convert date range option to actual dates
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    
    switch (selectedDateRange) {
      case "today":
        startDate = today;
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "last 7 days":
      case "last_7_days":
      case "this_week":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "last_30_days":
      case "this_month":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 29);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        startDate = today;
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
    }
  }

  // Create time chunks
  const timeChunks = createTimeChunks(startDate, endDate);

  const baseParams = {
    organizationId: orgId,
    dateOption: "custom", // Always use custom with time chunks
    allVehicles: true,
    allRoutes: false,
    allDrivers: false,
    allStops: false,
    allSites: false,
  };

  const reportTypes = [
    { type: "overspeed", url: `${config.baseURL}/report/overspeed` },
    { type: "activity", url: `${config.baseURL}/report/activity` },
    { type: "runtime", url: `${config.baseURL}/report/runtime` },
    { type: "halt", url: `${config.baseURL}/report/halt` },
    { type: "trip", url: `${config.baseURL}/report/trip` },
  ];

  return reportTypes.map(report => {
    const categories = report.type === "activity" 
      ? ["vehicles", "routes"] 
      : ["vehicles"];

    return categories.map(category => ({
      key: `${report.type}_${category}`,
      totalChunks: timeChunks.length,
      fetchNextChunk: async (chunkIndex, onChunkComplete) => {
        if (chunkIndex >= timeChunks.length) {
          return null; // No more chunks
        }

        const chunk = timeChunks[chunkIndex];
        const chunkParams = {
          ...baseParams,
          customStartDate: chunk.startDate,
          customEndDate: chunk.endDate
        };

        // Fetch all pages for this chunk
        let allChunkData = [];
        let page = 1;
        let hasMore = true;
        let maxSpeed = null;

        while (hasMore) {
          const result = await fetchPaginatedData(report.url, chunkParams, page);
          allChunkData = [...allChunkData, ...(result.data || [])];
          
          // Store maxSpeed from first response
          if (page === 1 && result.maxSpeed) {
            maxSpeed = result.maxSpeed;
          }

          hasMore = result.hasMore;
          page++;
        }

        // Call the callback with chunk data immediately
        if (onChunkComplete) {
          onChunkComplete({
            chunkIndex,
            data: allChunkData,
            maxSpeed,
            isLastChunk: chunkIndex === timeChunks.length - 1
          });
        }

        return {
          data: allChunkData,
          maxSpeed,
          chunkIndex,
          totalChunks: timeChunks.length
        };
      }
    }));
  }).flat();
};